import { Children, ReactElement, useCallback, useMemo } from 'react';

import isEqual from 'fast-deep-equal';

import { getConditionResult } from '@hsp/react-component-library';
import { useToggle } from '@hsp/react-hooks';
import { isDefined, isEmpty, isNotDefined, isNotEmpty, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useBuildAsyncValue, useFieldValues, useSavedState } from 'pages/audience/shared/hooks';
import { SaveAction } from 'pages/audience/shared/save/components/button/components/action';
import { useSaveButtonConfiguration } from 'pages/audience/shared/save/components/button/hooks';
import { useSaveStoreSelector, useSaveStoreSet } from 'pages/audience/shared/save/hooks/context';
import { selectAudienceCartShop, selectEmail, selectUserId } from 'store/selectors';
import { AudienceCategory } from 'types/api/audience';
import { SaveActionItem, SaveActionProps, SaveFeatures, SaveProps } from 'types/pages/audience-builder/shared/save';

export const useDefaultButtonConfiguration = ({ children, onOpen }: Pick<SaveProps, 'children' | 'onOpen'>) => {
  const userAudience = useAppSelector(selectAudienceCartShop, isEqual);
  const email = useAppSelector(selectEmail);
  const userId = useAppSelector(selectUserId);

  const features = useSaveStoreSelector(({ features }) => features);

  const setSaveStore = useSaveStoreSet();

  const asyncValue = useBuildAsyncValue();
  const { advanced } = useFieldValues();
  const { onClick } = useSaveButtonConfiguration({ features, onOpen });
  const { isDisabled } = useSavedState();
  const [isOpen, toggleIsOpen] = useToggle(false);

  const childrenArray = Children.toArray(children);

  const categoryId = useMemo(() => {
    if (isNotDefined(userAudience)) {
      return AudienceCategory.Basic;
    }

    return userAudience.categoryId;
  }, [userAudience]);
  const ownerId = useMemo(() => (isNotDefined(userAudience) ? undefined : userAudience.userId), [userAudience]);

  const designatedUser = useMemo(() => {
    const setting = process.env.REACT_APP_MASTERFILE_DESIGNATED_USER;

    if (isDefined(setting)) {
      const users = setting.split(',').filter(isNotEmpty);

      if (isEmpty(users)) {
        return true;
      }

      return isDefined(users.find((value) => value === email));
    }

    return true;
  }, [email]);

  const notMasterfileOwner = useMemo(() => {
    if (categoryId === AudienceCategory.MasterFile && ownerId !== userId) {
      return true;
    }

    return false;
  }, [categoryId, ownerId, userId]);

  const handleSave = useCallback(() => {
    let isAudience = false;

    if (isDefined(asyncValue)) {
      const { type } = asyncValue;

      isAudience = type === 'audience';
    }

    if (isAudience) {
      setSaveStore({ saveTo: true });
    }
    onClick({ saveTo: isAudience });
  }, [asyncValue, onClick, setSaveStore]);

  const handleSaveAsMaster = useCallback(() => {
    setSaveStore({ features: SaveFeatures.ShopMasterFile, saveAsMaster: true, saveAsPreBuilt: false, saveTo: false });
    onClick({ saveAsMaster: true, saveAsPreBuilt: false });
  }, [onClick, setSaveStore]);

  const handleSaveAsPreBuilt = useCallback(() => {
    setSaveStore({ features: SaveFeatures.ShopPreBuilt, saveAsMaster: false, saveAsPreBuilt: true, saveTo: false });
    onClick({ saveAsMaster: false, saveAsPreBuilt: true });
  }, [onClick, setSaveStore]);

  const handleSaveTo = useCallback(() => {
    setSaveStore({ features: SaveFeatures.Shop, saveAsMaster: false, saveAsPreBuilt: false, saveTo: true });
    onClick({ saveTo: true });
  }, [onClick, setSaveStore]);

  const actions = childrenArray.filter((child) => {
    const { props, type } = child as ReactElement<SaveActionProps>;
    let allow = true;

    if (isOfType<SaveActionItem>(props, 'condition')) {
      const { condition } = props;

      allow = isDefined(condition) ? getConditionResult(condition) : true;
    }

    return type === SaveAction && allow;
  });

  return {
    actions,
    advanced,
    designatedUser,
    handleSave,
    handleSaveAsMaster,
    handleSaveAsPreBuilt,
    handleSaveTo,
    isDisabled,
    isOpen,
    notMasterfileOwner,
    toggleIsOpen,
  };
};
